import React from 'react';
import styled from 'styled-components';
import { AiOutlineButton } from 'src/components/widgets/ai';
import Stack from 'src/components/widgets/stack/stack';
import { ChatBubble, ChatInputArea, ChatInput } from './chatPrimitives';

export const MessageGroup = styled.div<{ $merchant?: boolean }>`
    display: flex;
    align-items: ${(props) => (props.$merchant ? 'flex-end' : 'flex-start')};
    flex-direction: column;
    gap: 6px;
`;

export const AgentBubble = styled(ChatBubble)`
    max-width: min(92%, 720px);
    white-space: normal;
    &[aria-invalid='true'] {
        border-color: #fda29b;
    }
`;

export const AgentMarkdown = styled.div`
    overflow-wrap: anywhere;

    img {
        max-width: 100%;
        height: auto;
    }

    p {
        margin: 0 0 8px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }

    li + li {
        margin-top: 4px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 12px 0 6px;
        color: #263238;
        line-height: 1.3;
    }

    h1 {
        font-size: 18px;
    }

    h2 {
        font-size: 16px;
    }

    h3,
    h4,
    h5,
    h6 {
        font-size: 14px;
    }

    blockquote {
        margin: 8px 0;
        padding-left: 10px;
        border-left: 3px solid #cfd8dc;
        color: #607d8b;
    }

    code {
        padding: 1px 4px;
        border-radius: 4px;
        background: #eceff1;
        font-family: Consolas, Monaco, monospace;
        font-size: 0.92em;
    }

    pre {
        overflow-x: auto;
        margin: 8px 0;
        padding: 10px;
        border-radius: 7px;
        background: #eceff1;

        code {
            padding: 0;
            background: transparent;
        }
    }

    a {
        color: #7b1fa2;
        text-decoration: underline;
        overflow-wrap: anywhere;
    }
`;

export const MerchantBubble = styled(ChatBubble)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: ${(props) => props.theme.app.backgroundToolbar};
    color: ${(props) => props.theme.app.textColor};
`;

export const MessageText = styled.div`
    white-space: pre-wrap;
`;

export const ComposerInputArea = styled(ChatInputArea)`
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    padding: 10px;
    padding-top: 10px;

    &:focus-within {
        border-color: ${(props) => props.theme.ai.solid};
    }

    /* The field carries no border of its own; the container provides it. Border-box so the badge's top padding does
       not change the field height while requesting. */
    ${ChatInput} {
        box-sizing: border-box;
        border: 0;
        border-radius: 0;
        padding: 2px 4px;
        resize: none;

        &:focus {
            border-color: transparent;
        }
    }
`;

export function ConversationMessage({
    role,
    failed,
    label,
    children
}: {
    role: 'user' | 'assistant';
    failed?: boolean;
    label: string;
    children: React.ReactNode;
}) {
    const Bubble = role === 'user' ? MerchantBubble : AgentBubble;
    return (
        <MessageGroup $merchant={role === 'user'} role={failed ? 'alert' : undefined} aria-label={label}>
            <Bubble $role={role} aria-invalid={failed || undefined}>
                <Stack $gap='12px'>{children}</Stack>
            </Bubble>
        </MessageGroup>
    );
}

// Wraps the prompt field so the "requesting changes" badge can sit inside it, top-left. While requesting, the field
// grows taller and leaves room at the top for the badge.
export const ComposerField = styled.div<{ $requesting: boolean }>`
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;

    ${ChatInput} {
        min-height: 44px;

        /* The field keeps its height while requesting; only the top padding grows to clear the badge. */
        ${(props) =>
            props.$requesting &&
            `
            padding-top: 32px;
        `}
    }
`;

export const ComposerBottomRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ComposerControls = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 6px;
    margin-left: auto;
`;

export const ComposerAttachButton = styled(AiOutlineButton)`
    flex-shrink: 0;
`;
